import { type ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import clsx from 'clsx'
import type { BookingFormValues } from '../types/booking'

const CustomerForm = () => {
  const {
    register,
    formState: { errors },
  } = useFormContext<BookingFormValues>()

  return (
    <section aria-labelledby="customer-form-heading" className="superz-widget-panel p-6">
      <header className="mb-4">
        <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Contact Details</p>
        <h2 id="customer-form-heading" className="font-display text-3xl text-widget-accent">
          Your Details
        </h2>
        <p className="mt-2 text-sm text-widget-accent/70">
          Please provide the contact details for your private consultation confirmation.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label="Full Name"
          htmlFor="name"
          error={errors.name?.message}
          required
        >
          <input
            id="name"
            type="text"
            {...register('name')}
            className={inputClassName(errors.name)}
            placeholder="Your full name"
            autoComplete="name"
            required
          />
        </Field>

        <Field
          label="Email Address"
          htmlFor="email"
          error={errors.email?.message}
          required
        >
          <input
            id="email"
            type="email"
            {...register('email')}
            className={inputClassName(errors.email)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </Field>

        <Field
          label="Phone Number"
          htmlFor="phone"
          error={errors.phone?.message}
          hint="Hungarian format: +36 or 06"
          required
        >
          <input
            id="phone"
            type="tel"
            {...register('phone')}
            className={inputClassName(errors.phone)}
            placeholder="+36 30 123 4567"
            autoComplete="tel"
            required
          />
        </Field>
      </div>
    </section>
  )
}

interface FieldProps {
  label: string
  htmlFor: string
  children: ReactNode
  error?: string
  hint?: string
  required?: boolean
}

const Field = ({ label, htmlFor, children, error, hint, required }: FieldProps) => (
  <label className="flex flex-col gap-2" htmlFor={htmlFor}>
    <span className="uppercase tracking-[0.3em] text-xs text-widget-accent/60">
      {label}
      {required && <span className="text-widget-secondary"> *</span>}
    </span>
    {children}
    {hint && !error && <span className="text-xs text-widget-accent/60">{hint}</span>}
    {error && (
      <span role="alert" className="text-xs text-red-200">
        {error}
      </span>
    )}
  </label>
)

const inputClassName = (hasError?: unknown) =>
  clsx(
    'w-full rounded-2xl border bg-widget-primary/15 px-4 py-3 text-base text-widget-accent placeholder:text-widget-accent/40 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-widget-secondary/30 focus:border-widget-secondary',
    hasError ? 'border-red-300/60' : 'border-widget-primary/40',
  )

export default CustomerForm

